// Catálogo de "vistas" para el modo Catálogo IA.
//
// Una "vista" es un encuadre/ángulo predefinido con el que el backend
// genera el producto. Existen dos categorías:
//
//   • con-modelo  → la vista es una POSE del catálogo de poses (tabla
//                   `poses`). Para Catálogo IA solo usamos poses de
//                   1 persona. Payload backend: `composicion: 'modelo'`
//                   + `modelo_mode: 'ia'` + `pose: [id]`.
//
//   • solo-producto → la vista es un ángulo del producto sin modelo,
//                     replicando la lista de `getVistaOptions()` de
//                     `PruebaNuevaInterfaz`. Payload backend:
//                     `composicion: 'producto'` + `vista_producto: [id]`.
//                     Solo aplicable cuando el producto tiene cierto
//                     `model_view` — no todas las vistas valen para
//                     todos los productos.
//
// El catálogo de vistas "solo-producto" depende de `model_view` del
// producto. Replicamos exactamente la lógica de PruebaNuevaInterfaz
// para que el backend reciba ids que ya sabe procesar.

export type VistaCatalogoKind = 'con-modelo' | 'solo-producto' | 'mis-pose';

export interface VistaCatalogo {
  /** Id que viaja en el payload. Para con-modelo es el id de la pose;
   *  para solo-producto es el slug ('lateral', 'frente', etc.); para
   *  mis-pose es el id de la fila en `poses_personalizadas`. */
  id: string;
  /** Solo mis-pose: nombre del archivo del muñeco en storage
   *  (`<id_company>/poses_custom/<filename>`). Viaja en
   *  `poses_custom:[{id,filename}]` para que n8n baje el muñeco. */
  filename?: string;
  /** Nombre legible para mostrar en el picker / cards. */
  nombre: string;
  /** Imagen thumbnail default. Para con-modelo es la imagen de la pose;
   *  para solo-producto, una imagen de referencia (cuando exista). */
  imageUrl: string | null;
  /** Override por `model_view` — si el producto seleccionado matchea
   *  una key, se usa este URL en lugar del `imageUrl` default. Útil
   *  para vistas universales (`frente`, `posterior`) cuyo thumbnail
   *  conceptual cambia según la categoría: foto plana de zapatilla vs.
   *  foto plana de ropa, por ejemplo. */
  imageUrlByModelView?: Record<string, string>;
  /** Categoría a la que pertenece. */
  kind: VistaCatalogoKind;
  /** Solo aplica a solo-producto: lista de `model_view` con los que la
   *  vista es compatible. La vista se muestra como card solo si el
   *  producto matchea alguno de estos. `null` = compatible con todos. */
  compatibleModelViews?: string[] | null;
  /** Solo aplica a con-modelo (poses): "tipos de productos sugeridos"
   *  para esa pose — las áreas del cuerpo que destaca (`areas_principales`
   *  de la tabla `poses`), con el mismo vocabulario que el `model_view`
   *  de los productos (`calzado`, `torso`, `lentes`, `cabeza`, …). La pose
   *  se muestra/combina solo con productos cuyo `model_view` esté en esta
   *  lista — una pose que enfoca la cabeza no aplica a un zapato. Vacío o
   *  `undefined` = se trata como universal (row sin dato). */
  areasPrincipales?: string[];
}

const VISTA_BASE = '/images/multi-vistas/zapatillas';
const VISTA_BASE_OTROS = '/images/multi-vistas/otros';
const CLOTHING_MODEL_VIEWS = [
  'torso',
  'piernas',
  'cuerpo-completo',
  'cuerpo_completo',
];
/** Cuando el preset / producto pertenece a una categoría de ropa,
 *  preferimos los thumbs en `/otros/` (foto plana real de prenda)
 *  sobre los `/zapatillas/` que vienen como default genérico. */
const clothingImageOverride = (fileName: string): Record<string, string> =>
  Object.fromEntries(
    CLOTHING_MODEL_VIEWS.map((mv) => [mv, `${VISTA_BASE_OTROS}/${fileName}`]),
  );

/**
 * Catálogo de vistas "solo-producto" — replica exactamente las
 * opciones que devuelve `getVistaOptions()` en PruebaNuevaInterfaz,
 * pero con metadatos de compatibilidad para que el card-builder pueda
 * filtrar inteligentemente.
 *
 * Ojo: los IDs son slugs (`'frente'`, `'lateral'`, etc.). En PruebaNueva
 * lo que se manda al backend es el slug, no un UUID — repetimos lo mismo
 * acá para no romper la API.
 */
export const VISTAS_SOLO_PRODUCTO: VistaCatalogo[] = [
  // Vistas de calzado/cabeza — con imágenes de referencia disponibles.
  {
    id: 'lateral',
    nombre: 'Lateral',
    imageUrl: `${VISTA_BASE}/lateral.webp`,
    kind: 'solo-producto',
    compatibleModelViews: ['calzado', 'cabeza'],
  },
  {
    id: 'superior',
    nombre: 'Superior',
    imageUrl: `${VISTA_BASE}/superior.webp`,
    kind: 'solo-producto',
    compatibleModelViews: ['calzado', 'cabeza'],
  },
  {
    id: 'perspectiva',
    nombre: 'Perspectiva',
    imageUrl: `${VISTA_BASE}/perspectiva.webp`,
    kind: 'solo-producto',
    compatibleModelViews: ['calzado', 'cabeza'],
  },
  // Vistas universales — plano frente y plano posterior aplican a casi
  // todo. Renombrados a "Plano frente / Plano posterior" para alinear
  // con la nomenclatura del modo personalizado de PruebaNueva — en ropa
  // distingue mejor el "plano sobre fondo" del "maniquí invisible". El
  // thumbnail default es la zapatilla; en categorías de ropa el override
  // muestra la foto plana de prenda (otros/plano_*).
  {
    id: 'frente',
    nombre: 'Plano frente',
    imageUrl: `${VISTA_BASE}/frente.webp`,
    imageUrlByModelView: clothingImageOverride('plano_delantero.jpeg'),
    kind: 'solo-producto',
    compatibleModelViews: null,
  },
  {
    id: 'posterior',
    nombre: 'Plano posterior',
    imageUrl: `${VISTA_BASE}/posterior.webp`,
    imageUrlByModelView: clothingImageOverride('plano_trasero.jpeg'),
    kind: 'solo-producto',
    compatibleModelViews: null,
  },
  // Maniquí invisible (ghost mannequin) — solo aplica a prendas. La
  // foto del producto sale "vestida" como si la llevara puesta una
  // persona pero sin persona visible. Tres ángulos: frontal, trasero y
  // 3/4 perspectiva. Como ya están restringidas a clothing-only,
  // ponemos directamente el thumb correspondiente en `imageUrl`.
  {
    id: 'maniqui_frontal',
    nombre: 'Maniquí invisible frontal',
    imageUrl: `${VISTA_BASE_OTROS}/maniqui_frente.jpeg`,
    kind: 'solo-producto',
    compatibleModelViews: CLOTHING_MODEL_VIEWS,
  },
  {
    id: 'maniqui_trasero',
    nombre: 'Maniquí invisible trasero',
    imageUrl: `${VISTA_BASE_OTROS}/maniqui_trasero.jpeg`,
    kind: 'solo-producto',
    compatibleModelViews: CLOTHING_MODEL_VIEWS,
  },
  {
    id: 'maniqui_angulo',
    nombre: 'Maniquí invisible ángulo',
    imageUrl: `${VISTA_BASE_OTROS}/maniqui_angulo.jpeg`,
    kind: 'solo-producto',
    compatibleModelViews: CLOTHING_MODEL_VIEWS,
  },
  // Vista automática — solo para productos non-clothing/non-shoe en
  // PruebaNueva. La dejamos pero la marcamos solo para esos
  // model_views.
  {
    id: 'automatica',
    nombre: 'Automática',
    imageUrl: null,
    kind: 'solo-producto',
    compatibleModelViews: [
      'accesorio_cuello',
      'manos',
      'lentes',
      'aros',
      'producto',
      'objeto',
    ],
  },
];

/**
 * Resuelve el thumbnail a usar para una vista según el contexto. Si la
 * vista tiene un `imageUrlByModelView` y el `modelView` provisto matchea
 * alguna key (normalizando `_`/`-`), usa ese override. Caso contrario,
 * cae al `imageUrl` default. Se le puede pasar una lista de model_views
 * (por ejemplo el `dominantModelView` calculado a partir de los productos
 * seleccionados) — el primero que matchee gana.
 */
export const resolveVistaImageUrl = (
  vista: VistaCatalogo,
  modelView: string | null | undefined,
): string | null => {
  if (!modelView || !vista.imageUrlByModelView) return vista.imageUrl;
  const norm = modelView.replace(/-/g, '_');
  for (const [key, url] of Object.entries(vista.imageUrlByModelView)) {
    if (key.replace(/-/g, '_') === norm) return url;
  }
  return vista.imageUrl;
};

/**
 * Chequea si una vista es compatible con el `model_view` de un producto.
 *
 *   • con-modelo (pose)   → mira `areasPrincipales` (tipos de producto
 *                           sugeridos de la pose). Una pose que enfoca la
 *                           cabeza no aplica a un zapato; una que enfoca
 *                           los pies sí.
 *   • solo-producto       → mira `compatibleModelViews`.
 *
 * Si la lista correspondiente está vacía o ausente (dato faltante), se
 * asume universal para no ocultar todo por un row incompleto.
 */
export const isVistaCompatibleConModelView = (
  vista: VistaCatalogo,
  modelView: string | null | undefined,
): boolean => {
  // mis-pose (poses custom del muñeco) son universales: el cliente las
  // creó a propósito, las combinamos con cualquier producto. No tienen
  // `areasPrincipales` ni `compatibleModelViews`.
  if (vista.kind === 'mis-pose') return true;
  const allowed =
    vista.kind === 'con-modelo'
      ? vista.areasPrincipales
      : vista.compatibleModelViews;
  if (!allowed || allowed.length === 0) return true;
  if (!modelView) {
    // Producto SIN model_view (tipo desconocido). No podemos descartar por
    // tipo, así que para POSES (con-modelo) las mostramos TODAS — igual que
    // PruebaNuevaInterfaz cuando falta el model_view. Para vistas
    // solo-producto mantenemos solo las universales (frente/posterior, que ya
    // pasaron por el `!allowed` de arriba) y NO sumamos maniquí/ángulos, que
    // dependen de la categoría. Sin esto, un producto sin model_view quedaba
    // sin NINGUNA pose en el catálogo.
    return vista.kind === 'con-modelo';
  }
  // Normalizamos guion-medio vs guion-bajo (y case) porque algunos
  // productos vienen con 'cuerpo-completo' y otros con 'cuerpo_completo'.
  const norm = modelView.replace(/-/g, '_').toLowerCase();
  return allowed.some((c) => c.replace(/-/g, '_').toLowerCase() === norm);
};
